import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  Braces,
  Database,
  Download,
  ExternalLink,
  Github,
  ShieldCheck,
  Users,
} from "lucide-react";

import SEO from "../components/SEO";
import ProductSystemMap from "../components/ProductSystemMap";
import TechConstellation from "../components/TechConstellation";
import { Button } from "../components/ui/button";
import { Container } from "../components/layout/container";
import { useLanguage } from "../context/LanguageContext";
import { getBuildLabProjects, getBuildLabUrl } from "../data/buildLab";
import { getProjectsData } from "../data/projectsData";
import { getResumePath } from "../data/resume";

const outcomes = [
  { value: "10+", label: "Years building products", note: "Data, AI, platforms" },
  { value: "25+", label: "Products shipped", note: "Zero to one and scale" },
  { value: "5M+", label: "Users reached", note: "Internal and external" },
  { value: "40%", label: "Support time reduced", note: "AI workflow outcome" },
  { value: "70%", label: "L1 self-serve resolution", note: "RAG support workflow" },
  { value: "75%", label: "Critical incidents reduced", note: "Automated data quality" },
  { value: "100%", label: "Leadership adoption", note: "Unified KPI platform" },
  { value: "6 wks", label: "Time to first revenue", note: "Solo-built SaaS" },
];

const capabilities = [
  {
    title: "Data Product Management",
    description:
      "I define trusted metrics, shape semantic layers, and turn fragmented reporting into products teams can operate from.",
    proof: "Unified KPI platform with leadership-wide adoption",
    displayTitle: "Turn data into a shared operating language.",
    narrative: "I align definitions, ownership, and product decisions so teams can trust and act on the same signal.",
    signal: "Data product system",
    flow: ["Sources", "Semantics", "Decisions"],
    icon: Database,
  },
  {
    title: "AI-native Product Development",
    description:
      "I design RAG and agent workflows around evaluation, guardrails, human review, cost, and real operating constraints.",
    proof: "50K+ monthly queries with 70% L1 self-serve resolution",
    displayTitle: "Build AI around the workflow, not the demo.",
    narrative: "I treat the model as one component in a reliable product with evaluation, guardrails, and human review.",
    signal: "AI product system",
    flow: ["Context", "Evaluate", "Assist"],
    icon: Bot,
  },
  {
    title: "Forward-deployed Execution",
    description:
      "I work inside user workflows, connect product decisions to system realities, and stay through deployment and adoption.",
    proof: "Embedded IT business partner for South Asia commercial operations",
    displayTitle: "Stay close until the product is adopted.",
    narrative: "I work inside the user's environment, connect the systems, and own the last mile from deployment to behavior change.",
    signal: "Forward-deployed loop",
    flow: ["Embed", "Integrate", "Adopt"],
    icon: Users,
  },
  {
    title: "Solo Building",
    description:
      "I scope, prototype, code, and publish working tools with AI-assisted development across TypeScript, JavaScript, and Python.",
    proof: "Public AI, data, and developer-tool projects on GitHub",
    displayTitle: "Shorten the distance from strategy to software.",
    narrative: "I prototype and ship the product myself when a working artifact is the fastest way to learn.",
    signal: "Solo build loop",
    flow: ["Scope", "Build", "Ship"],
    icon: Braces,
  },
];

const aiPrinciples = [
  {
    number: "01",
    title: "Start with the workflow",
    description: "The model is one component. The product starts with the decision or task a user needs to complete.",
  },
  {
    number: "02",
    title: "Evaluate before scaling",
    description: "Define quality, safety, latency, and cost thresholds before expanding traffic or autonomy.",
  },
  {
    number: "03",
    title: "Design the human handoff",
    description: "Make uncertainty visible and give people a clear path to review, correct, or escalate an output.",
  },
  {
    number: "04",
    title: "Measure adoption in context",
    description: "Track whether the product improves the workflow, not only whether the AI produced an answer.",
  },
];

const testimonials = [
  {
    name: "Todd Schecter",
    title: "Databricks Consulting Growth Leader",
    quote:
      "Exceptional product leader. Breathtaking pace of innovation. Always finds time to engage with sales and strategic customers.",
  },
  {
    name: "Thangadurai Elangovan",
    title: "AGM and Tech Lead, Tradebees Beijing",
    quote:
      "Highly skilled in big data. Proactive problem-solver. Calm under pressure and a true asset to the team.",
  },
  {
    name: "Lola Han Luo",
    title: "Senior Product Manager, FlashIntel",
    quote:
      "The word dedication personified. Energetic, with excellent product management skills. Proud of what we achieved together.",
  },
];

const heroNodes = [
  { label: "User workflow", detail: "Observe the constraint", icon: Users },
  {
    label: "Trusted data",
    detail: "Define the signal",
    stack: "PostgreSQL / dbt / Snowflake",
    stage: "data",
    icon: Database,
  },
  {
    label: "AI system",
    detail: "Build and evaluate",
    stack: "OpenAI / Python / FastAPI",
    stage: "ai",
    icon: Bot,
  },
  {
    label: "Shipped product",
    detail: "Deploy and measure",
    stack: "React / TypeScript / Vercel",
    stage: "ship",
    icon: Braces,
  },
];

function HeroWorkbench({ activeStage }) {
  const reduceMotion = useReducedMotion();
  const { strings } = useLanguage();
  const wb = strings?.home?.hero?.workbench;
  const currentNodes = wb?.nodes?.map((node, i) => ({
    ...node,
    icon: heroNodes[i]?.icon,
    stack: node.stack || heroNodes[i]?.stack,
    stage: node.stage || heroNodes[i]?.stage,
  })) || heroNodes;

  return (
    <div className="pointer-events-auto relative mx-auto w-full max-w-lg lg:ml-auto">
      <div className="absolute -inset-8 rounded-full bg-[#8AB4FF]/10 blur-3xl" aria-hidden="true" />
      <div className="relative overflow-hidden rounded-3xl border border-white/[0.12] bg-[#111118]/95 p-5 shadow-2xl shadow-black/40 sm:p-7">
        <div className="flex items-center justify-between border-b border-white/[0.08] pb-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">{wb?.title || "Product workbench"}</p>
            <p className="mt-1 text-sm text-white/75">{wb?.sub || "Problem to production"}</p>
          </div>
          <span className="rounded-full border border-[#8AB4FF]/30 bg-[#8AB4FF]/10 px-3 py-1 text-xs text-[#B7D0FF]">
            {wb?.tag || "AI-native"}
          </span>
        </div>

        <div className="relative mt-6 space-y-3">
          <div className="hero-pipeline-line absolute bottom-7 left-9 top-7" aria-hidden="true" />
          {currentNodes.map((node, index) => {
            const Icon = node.icon;
            const isActive = activeStage === node.stage;
            return (
              <motion.div
                key={node.label}
                className={`relative flex items-center gap-4 rounded-2xl border px-4 py-4 transition-[background-color,border-color,box-shadow] duration-150 ${
                  isActive
                    ? "border-[#8AB4FF]/55 bg-[#8AB4FF]/10 shadow-[0_0_0_1px_rgba(138,180,255,0.08)]"
                    : "border-white/[0.08] bg-white/[0.035]"
                }`}
                initial={reduceMotion ? false : { opacity: 0, transform: "translateY(8px)" }}
                animate={{ opacity: 1, transform: "translateY(0)" }}
                transition={{
                  duration: reduceMotion ? 0.1 : 0.36,
                  delay: reduceMotion ? 0 : 0.15 + index * 0.07,
                  ease: [0.23, 1, 0.32, 1],
                }}
              >
                <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#8AB4FF]/30 bg-[#141925] text-[#B7D0FF]">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{node.label}</p>
                  <p className={`mt-0.5 text-xs ${isActive ? "text-[#B7D0FF]" : "text-white/45"}`}>
                    {isActive ? node.stack : node.detail}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-5 flex items-center justify-between rounded-2xl bg-[#8AB4FF] px-4 py-3 text-[#08101F]">
          <span className="text-sm font-semibold">{wb?.outcome || "Outcome"}</span>
          <span className="text-sm font-medium">{wb?.adoptedWorkflow || "Adopted workflow"}</span>
        </div>
      </div>
    </div>
  );
}

function OutcomeStrip() {
  const { strings } = useLanguage();
  const ticker = strings?.home?.ticker;
  const currentOutcomes = ticker?.outcomes || outcomes;

  return (
    <section className="border-b border-white/[0.08] py-6" aria-labelledby="impact-rail-title">
      <Container>
        <div className="mb-4 flex items-center justify-between gap-4">
          <p id="impact-rail-title" className="text-xs font-semibold uppercase tracking-[0.16em] text-white/40">
            {ticker?.title || "Impact across shipped systems"}
          </p>
          <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#8AB4FF]/70">
            {ticker?.feed || "Impact feed"}
          </span>
        </div>

        <div className="impact-ticker border-y border-white/[0.08]" role="region" aria-label="Automatically scrolling impact metrics">
          <div className="impact-ticker-track">
            {[false, true].map((isDuplicate) => (
              <div
                key={isDuplicate ? "duplicate" : "primary"}
                className="impact-ticker-group"
                aria-hidden={isDuplicate ? "true" : undefined}
              >
                {currentOutcomes.map((outcome) => (
                  <article key={`${isDuplicate ? "duplicate" : "primary"}-${outcome.label}`} className="impact-ticker-item">
                    <p className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">{outcome.value}</p>
                    <p className="mt-2 text-sm font-medium text-white/75">{outcome.label}</p>
                    <p className="mt-1 text-xs text-white/40">{outcome.note}</p>
                  </article>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function CapabilitySignal({ capability, reduceMotion }) {
  const Icon = capability.icon;
  const { strings } = useLanguage();

  return (
    <motion.div
      className="mt-8 overflow-hidden rounded-3xl border border-white/10 bg-[#111118] p-5 shadow-xl shadow-black/20 sm:p-6"
      initial={reduceMotion ? false : { opacity: 0, transform: "translateY(8px)" }}
      animate={{ opacity: 1, transform: "translateY(0)" }}
      exit={reduceMotion ? { opacity: 0 } : { opacity: 0, transform: "translateY(-6px)" }}
      transition={{ duration: reduceMotion ? 0.1 : 0.22, ease: [0.23, 1, 0.32, 1] }}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#8AB4FF]/30 bg-[#8AB4FF]/10 text-[#B7D0FF]">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-white/35">
            {strings?.home?.activeSystem || "Active system"}
          </p>
          <p className="mt-1 text-sm font-semibold text-white">{capability.signal}</p>
        </div>
      </div>

      <div className="mt-6 flex items-center">
        {capability.flow.map((step, index) => (
          <React.Fragment key={step}>
            <div className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/[0.035] px-2 py-3 text-center">
              <p className="font-mono text-[10px] text-[#8AB4FF]">0{index + 1}</p>
              <p className="mt-1 truncate text-xs font-medium text-white/75">{step}</p>
            </div>
            {index < capability.flow.length - 1 && (
              <div className="relative h-px w-5 shrink-0 overflow-hidden bg-white/10 sm:w-7" aria-hidden="true">
                <motion.span
                  className="absolute inset-0 origin-left bg-[#8AB4FF]/70"
                  initial={reduceMotion ? false : { transform: "scaleX(0)" }}
                  animate={{ transform: "scaleX(1)" }}
                  transition={{
                    duration: reduceMotion ? 0.1 : 0.32,
                    delay: reduceMotion ? 0 : 0.08 + index * 0.06,
                    ease: [0.77, 0, 0.175, 1],
                  }}
                />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-6 border-t border-white/[0.08] pt-5">
        <p className="text-xs font-medium text-white/35">
          {strings?.home?.evidence || "Evidence"}
        </p>
        <p className="mt-2 text-sm leading-6 text-[#B7D0FF]">{capability.proof}</p>
      </div>
    </motion.div>
  );
}

function CapabilitySection() {
  const [activeCapability, setActiveCapability] = useState(0);
  const reduceMotion = useReducedMotion();
  const { strings } = useLanguage();
  const capabilityIcons = [Database, Bot, Users, Braces];
  const currentCapabilities = strings?.home?.capabilities?.map((cap, i) => ({
    ...cap,
    icon: capabilityIcons[i] || capabilities[i]?.icon,
  })) || capabilities;
  const currentCapability = currentCapabilities[activeCapability] || currentCapabilities[0];

  return (
    <section id="about" className="py-20 sm:py-24 lg:py-28" aria-labelledby="capabilities-title">
      <span id="skills" className="block scroll-mt-24" aria-hidden="true" />
      <Container>
        <div className="grid gap-12 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:gap-20">
          <div className="lg:sticky lg:top-28 lg:h-fit">
            <p className="text-sm font-medium text-[#8AB4FF]">
              {strings?.home?.capabilitiesTitle || "What I bring"}
            </p>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentCapability.title}
                initial={reduceMotion ? false : { opacity: 0, transform: "translateY(8px)" }}
                animate={{ opacity: 1, transform: "translateY(0)" }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, transform: "translateY(-6px)" }}
                transition={{ duration: reduceMotion ? 0.1 : 0.22, ease: [0.23, 1, 0.32, 1] }}
              >
                <h2
                  id="capabilities-title"
                  className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl"
                >
                  {currentCapability.displayTitle}
                </h2>
                <p className="mt-5 max-w-md text-base leading-7 text-white/60">
                  {currentCapability.narrative}
                </p>
                <CapabilitySignal capability={currentCapability} reduceMotion={reduceMotion} />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="border-t border-white/10">
            {currentCapabilities.map((capability, index) => {
              const Icon = capability.icon;
              const isActive = activeCapability === index;
              return (
                <motion.article
                  key={capability.title}
                  className={`grid gap-4 border-b py-8 transition-[background-color,border-color] duration-200 sm:grid-cols-[auto_1fr] sm:gap-6 lg:min-h-[42vh] lg:items-center lg:px-5 ${
                    isActive
                      ? "border-[#8AB4FF]/25 bg-[#8AB4FF]/[0.025]"
                      : "border-white/10 bg-transparent"
                  }`}
                  onViewportEnter={() => setActiveCapability(index)}
                  viewport={{ margin: "-42% 0px -42% 0px" }}
                  aria-current={isActive ? "true" : undefined}
                >
                  <div className={`flex h-11 w-11 items-center justify-center rounded-xl border bg-white/[0.035] transition-[background-color,border-color,color] duration-200 ${
                    isActive
                      ? "border-[#8AB4FF]/35 bg-[#8AB4FF]/10 text-[#B7D0FF]"
                      : "border-white/10 text-white/45"
                  }`}>
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">{capability.title}</h3>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-white/60 sm:text-base">
                      {capability.description}
                    </p>
                    <p className="mt-4 text-sm font-medium text-[#B7D0FF]">
                      {strings?.home?.proofPrefix || "Proof: "}
                      {capability.proof}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>
      </Container>
    </section>
  );
}

function ProjectCard({ project, className = "" }) {
  const { strings } = useLanguage();
  const primaryMetric = project.impact.metrics[0];

  return (
    <article className={`group flex h-full flex-col rounded-3xl border border-white/10 bg-white/[0.035] p-6 transition-colors duration-200 hover:border-[#8AB4FF]/35 hover:bg-white/[0.055] sm:p-8 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-medium text-[#8AB4FF]">{project.category}</p>
        <p className="text-xs text-white/40">{project.role}</p>
      </div>
      <h3 className="mt-5 text-2xl font-semibold tracking-tight text-white">{project.title}</h3>
      <p className="mt-4 text-sm leading-6 text-white/60 sm:text-base">{project.summary}</p>

      <div className="mt-7 grid grid-cols-2 gap-4 border-y border-white/[0.08] py-5">
        <div>
          <p className="text-2xl font-semibold text-white">{primaryMetric.value}</p>
          <p className="mt-1 text-xs leading-5 text-white/45">{primaryMetric.label}</p>
        </div>
        <div>
          <p className="text-sm font-semibold text-white">{project.duration}</p>
          <p className="mt-1 text-xs leading-5 text-white/45">{project.team}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {project.tags.map((tag) => (
          <span key={tag} className="rounded-lg border border-white/10 bg-white/[0.035] px-2.5 py-1 text-xs text-white/50">
            {tag}
          </span>
        ))}
      </div>

      <Link
        to={`/case-study/${project.slug}`}
        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-white transition-colors duration-200 group-hover:text-[#B7D0FF]"
      >
        {strings?.home?.selectedWork?.readCaseStudy || "Read case study"}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </article>
  );
}

function SelectedWork() {
  const { language, strings } = useLanguage();
  const currentProjects = getProjectsData(language);
  const sw = strings?.home?.selectedWork;

  return (
    <section id="projects" className="py-20 sm:py-24 lg:py-28" aria-labelledby="selected-work-title">
      <Container>
        <div className="max-w-3xl">
          <p className="text-sm font-medium text-[#8AB4FF]">{sw?.eyebrow || "Selected work"}</p>
          <h2
            id="selected-work-title"
            className="mt-4 text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl lg:text-5xl"
          >
            {sw?.title || "Products that moved from ambiguity to impact."}
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-7 text-white/60">
            {sw?.subtitle || "Four stories across AI, analytics, data reliability, and solo product development."}
          </p>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-12">
          <ProjectCard project={currentProjects[0]} className="lg:col-span-7" />
          <ProjectCard project={currentProjects[1]} className="lg:col-span-5" />
          <ProjectCard project={currentProjects[2]} className="lg:col-span-5" />
          <ProjectCard project={currentProjects[3]} className="lg:col-span-7" />
        </div>
      </Container>
    </section>
  );
}

function BuildLab() {
  const { language, strings } = useLanguage();
  const currentProjects = getBuildLabProjects(language);
  const bl = strings?.home?.buildLab;

  return (
    <section id="build-lab" className="py-20 sm:py-24 lg:py-28" aria-labelledby="build-lab-title">
      <Container>
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)] lg:gap-20">
          <div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#8AB4FF]/30 bg-[#8AB4FF]/10 text-[#B7D0FF]">
              <Github className="h-6 w-6" aria-hidden="true" />
            </div>
            <h2 id="build-lab-title" className="mt-6 text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl">
              {bl?.title || "Build Lab"}
            </h2>
            <p className="mt-5 max-w-md text-base leading-7 text-white/60">
              {bl?.subtitle || "Public experiments where I learn by building. These repositories show the work, not just the job title."}
            </p>
            <a
              href="https://github.com/nandanosql"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#B7D0FF]"
            >
              {bl?.viewGithub || "View GitHub profile"}
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>

          <div className="grid gap-x-8 sm:grid-cols-2">
            {currentProjects.map((project) => (
              <article key={project.repository} className="border-t border-white/10 py-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-[#8AB4FF]">{project.signal}</p>
                    <h3 className="mt-2 text-lg font-semibold text-white">{project.name}</h3>
                  </div>
                  <span className="rounded-lg border border-white/10 px-2 py-1 text-[11px] text-white/40">
                    {project.language}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-6 text-white/55">{project.description}</p>
                <a
                  href={getBuildLabUrl(project.repository)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/75 hover:text-white"
                >
                  {bl?.repo || "Repository"}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </article>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function AIProductPrinciples() {
  const { strings } = useLanguage();
  const aip = strings?.home?.aiPrinciples;
  const currentPrinciples = aip?.principles || aiPrinciples;

  return (
    <section className="border-y border-white/[0.08] bg-white/[0.018] py-20 sm:py-24 lg:py-28" aria-labelledby="ai-principles-title">
      <Container>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04] text-[#B7D0FF]">
              <ShieldCheck className="h-6 w-6" aria-hidden="true" />
            </div>
            <h2 id="ai-principles-title" className="mt-6 text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl">
              {aip?.title || "How I ship responsible AI products."}
            </h2>
            <p className="mt-5 max-w-lg text-base leading-7 text-white/60">
              {aip?.subtitle || "AI-native means designing the entire operating system around the model, including failure, feedback, and adoption."}
            </p>
          </div>

          <div className="border-t border-white/10">
            {currentPrinciples.map((principle) => (
              <article key={principle.number} className="grid grid-cols-[auto_1fr] gap-5 border-b border-white/10 py-6">
                <span className="font-mono text-xs text-[#8AB4FF]">{principle.number}</span>
                <div>
                  <h3 className="text-lg font-semibold text-white">{principle.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-white/55">{principle.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

function TestimonialSection() {
  const { strings } = useLanguage();
  const testSection = strings?.home?.testimonials;
  const currentTestimonials = testSection?.items || testimonials;

  return (
    <section id="testimonials" className="py-20 sm:py-24 lg:py-28" aria-labelledby="testimonials-title">
      <Container>
        <div className="max-w-2xl">
          <h2 id="testimonials-title" className="text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl">
            {testSection?.title || "Trusted across product, data, and go-to-market teams."}
          </h2>
        </div>

        <div className="mt-12 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
          <figure className="flex min-h-[320px] flex-col justify-between rounded-3xl border border-[#8AB4FF]/25 bg-[#8AB4FF]/8 p-7 sm:p-10">
            <blockquote className="text-2xl font-medium leading-9 tracking-tight text-white sm:text-3xl sm:leading-10">
              “{currentTestimonials[0].quote}”
            </blockquote>
            <figcaption className="mt-10">
              <p className="font-semibold text-white">{currentTestimonials[0].name}</p>
              <p className="mt-1 text-sm text-white/50">{currentTestimonials[0].title}</p>
            </figcaption>
          </figure>

          <div className="grid gap-5">
            {currentTestimonials.slice(1).map((testimonial) => (
              <figure key={testimonial.name} className="flex flex-col justify-between rounded-3xl border border-white/10 bg-white/[0.035] p-7">
                <blockquote className="text-base leading-7 text-white/75">“{testimonial.quote}”</blockquote>
                <figcaption className="mt-8">
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="mt-1 text-sm text-white/45">{testimonial.title}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </Container>
    </section>
  );
}

export default function PortfolioPage() {
  const reduceMotion = useReducedMotion();
  const [activeTechStage, setActiveTechStage] = useState(null);
  const { language, strings } = useLanguage();
  const home = strings?.home;
  const seo = home?.seo;
  const hero = home?.hero;

  return (
    <div className="min-h-[100dvh] bg-[#0B0B0F] text-white">
      <SEO
        title={seo?.title || "Nandan Priyadarshi | Data PM, AI-native Builder and FDE"}
        description={seo?.description || "Data Product Manager and AI-native builder who works directly with users, models the data, ships production workflows, and measures adoption."}
        canonicalUrl="/"
        keywords={seo?.keywords || "Nandan Priyadarshi, Data Product Manager, AI Product Manager, AI-native Builder, Forward Deployed Engineer, FDE, Solo Builder, Data Products"}
      />

      <main>
        <section className="relative overflow-hidden border-b border-white/[0.08] py-14 sm:py-[4.5rem] lg:py-20" aria-label="Introduction">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_76%_28%,_rgba(138,180,255,0.14),_transparent_34%)]" aria-hidden="true" />
          <TechConstellation activeStage={activeTechStage} onStageChange={setActiveTechStage} />
          <Container className="pointer-events-none relative grid items-center gap-12 lg:grid-cols-[minmax(0,1.08fr)_minmax(420px,0.92fr)] lg:gap-14">
            <motion.div
              className="pointer-events-auto"
              initial={reduceMotion ? false : { opacity: 0, transform: "translateY(12px)" }}
              animate={{ opacity: 1, transform: "translateY(0)" }}
              transition={{ duration: reduceMotion ? 0.1 : 0.5, ease: [0.23, 1, 0.32, 1] }}
            >
              <p className="text-sm font-medium text-[#B7D0FF]">
                {hero?.badge || "Data PM / AI-native / Forward-deployed / Solo builder"}
              </p>
              <h1 className="mt-5 max-w-4xl text-4xl font-semibold tracking-[-0.045em] text-white sm:text-5xl lg:text-[3.25rem] lg:leading-[1.04]">
                {hero?.headline || "I build AI products teams actually adopt."}
              </h1>
              <p className="mt-6 max-w-xl text-base leading-7 text-white/65 sm:text-lg">
                {hero?.subheading || "I work with users, model the data, build the workflow, and ship production-ready tools."}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg">
                  <a href="#projects">
                    {hero?.viewWork || "View selected work"}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                </Button>
                <Button asChild size="lg" variant="secondary">
                  <a href={getResumePath(language)} target="_blank" rel="noopener noreferrer">
                    <Download className="h-4 w-4" aria-hidden="true" />
                    {hero?.downloadResume || "Download resume"}
                  </a>
                </Button>
              </div>
            </motion.div>

            <HeroWorkbench activeStage={activeTechStage} />
          </Container>
        </section>

        <OutcomeStrip />
        <CapabilitySection />
        <SelectedWork />
        <ProductSystemMap />
        <BuildLab />
        <AIProductPrinciples />
        <TestimonialSection />

        <section className="pb-24 pt-8 sm:pb-28" aria-labelledby="contact-cta-title">
          <Container>
            <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#12121A] px-6 py-12 sm:px-10 lg:flex lg:items-center lg:justify-between lg:gap-10 lg:px-14 lg:py-14">
              <div>
                <p className="text-sm font-medium text-[#8AB4FF]">
                  {strings?.home?.contactCta?.eyebrow || "Have a difficult product problem?"}
                </p>
                <h2 id="contact-cta-title" className="mt-3 max-w-2xl text-3xl font-semibold tracking-[-0.035em] text-white sm:text-4xl">
                  {strings?.home?.contactCta?.title || "Let’s turn the workflow into something people can use."}
                </h2>
              </div>
              <Button asChild size="lg" className="mt-8 shrink-0 lg:mt-0">
                <Link to="/contact-me">
                  {strings?.home?.contactCta?.button || "Let’s talk"}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </Container>
        </section>
      </main>
    </div>
  );
}
